import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";

import FacilityForm from "./FacilityForm";
import {
  fieldStateToUpdateArgs,
  validFieldState,
} from "../FieldStateFunctions";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import RequestCompleteNotice from "../../../../common/settings-modal/RequestCompleteNotice";
import { ImageSourceSetData } from "../../../../common/classes/ImageSourceSet";

import { useAppSelector } from "../../../../redux/hooks";
import { getAuthToken } from "../../../../redux/reducers/auth";

import {
  FieldState,
  valFileTotalSize,
} from "../../../../utils/ValidationFunctions";
import { RequestState } from "../../../../utils/RequestState";
import {
  useGetFacility,
  useRequestFacilityApproval,
  useUpdateFacility,
} from "../../../../utils/useFacility";
import { PageVisibility } from "../../../../utils/PageVisibility";
import {
  convertFacilityTraits,
  convertFacilityTypes,
} from "../../../../utils/FacilityState";

const EditFacilityContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [fieldState, setFieldState] = useState<FieldState>();
  const [imgItems, setImgItems] = useState<Array<File | ImageSourceSetData>>(
    []
  );
  const [deleteImg, setDeleteImg] = useState<Array<string>>([]);
  const [traits, setTraits] = useState<Array<string>>([]);
  const [types, setTypes] = useState<Array<string>>([]);
  const [requestState, setRequestState] = useState<RequestState>(
    RequestState.Waiting
  );
  const [approved, setApproved] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const auth_token = useAppSelector(getAuthToken);

  const queryArgs = {
    authToken: auth_token,
    per: -1,
  };

  const {
    items: { facilities },
    isLoading,
  } = useGetFacility(queryArgs);
  const facility = facilities.find((e) => e.id === id);

  const { mutateAsync, mutateLoading } = useUpdateFacility([
    "facility",
    queryArgs,
  ]);

  const { requestAsync, requestLoading } = useRequestFacilityApproval([
    "facility",
    queryArgs,
  ]);

  const completeHandler = () => {
    navigate("/mypage/facility-setting", { replace: true });
  };

  const handleSubmit = async () => {
    if (auth_token && id) {
      setRequestState(RequestState.Doing);

      await requestAsync({ auth_token, id });

      setApproved(true);
    }
  };

  const handleSave = async () => {
    if (!id) return;

    if (!validFieldState("edit", ["sonic_name"], fieldState)) {
      setError(true);
      return;
    }

    // 送信する画像の合計サイズを確認
    if (!valFileTotalSize(imgItems)) {
      setError(true);
      return;
    }

    setError(false);

    const update_args: { [key: string]: string | File } = {};

    fieldStateToUpdateArgs(update_args, fieldState);

    if (imgItems) {
      imgItems.forEach((v, i) => {
        if (v instanceof File) {
          update_args[`sonic_imgs${i}`] = v;
        }
      });
    }

    if (deleteImg) {
      deleteImg.forEach((v, i) => {
        update_args[`sonic_delete_imgs${i}`] = v;
      });
    }

    const update_traits = JSON.stringify(convertFacilityTraits(traits));
    if (update_traits !== JSON.stringify(facility?.traits)) {
      update_args["sonic_traits"] = update_traits;
    }

    const update_types = JSON.stringify(convertFacilityTypes(types));
    if (update_types !== JSON.stringify(facility?.facility_types)) {
      update_args["sonic_facility_types"] = update_types;
    }

    if (Object.keys(update_args).length && auth_token) {
      setRequestState(RequestState.Doing);
      update_args["sonic_id"] = id;

      await mutateAsync({ authToken: auth_token, update: update_args });
      setFieldState(undefined);
      return setRequestState(RequestState.Success);
    }

    setRequestState(RequestState.Success);
  };

  const handleInputChange = (
    value: File | string | undefined,
    name: string
  ) => {
    let f: FieldState = {};
    if (fieldState !== undefined) {
      f = fieldState;
    }

    if (value !== undefined) {
      if (value instanceof File) {
        setImgItems([...imgItems, value]);
      } else {
        f[name] = [value, true];
      }
      setRequestState(RequestState.Pending);
    } else {
      if (name !== "sonic_imgs") f[name] = ["", false];
    }
    setFieldState(f);
  };

  const deleteImgItem = (img: File | ImageSourceSetData, i: number) => {
    setRequestState(RequestState.Pending);

    if (!(img instanceof File)) {
      const del = deleteImg;
      if (!del.includes(img.id.toString())) {
        del.push(img.id.toString());

        setDeleteImg([...del]);
      }
    }

    const newList = imgItems.filter((v, index) => index !== i);
    setImgItems(newList);
  };

  const changeTraits = (value: string) => {
    setRequestState(RequestState.Pending);

    if (traits.includes(value)) {
      setTraits(traits.filter((trait) => trait !== value));
    } else {
      setTraits([...traits, value]);
    }
  };

  const changeTypes = (value: string) => {
    setRequestState(RequestState.Pending);

    if (types.includes(value)) {
      setTypes(types.filter((type) => type !== value));
    } else {
      setTypes([...types, value]);
    }
  };

  useEffect(() => {
    if (isLoading) return;

    setImgItems(facility?.imgs ? facility.imgs : []);
    setTypes(facility?.facility_types ? facility.facility_types : []);
    setTraits(facility?.traits ? facility.traits : []);
  }, [isLoading]);

  if (
    isLoading ||
    mutateLoading ||
    requestLoading ||
    (facility !== undefined && !(facility.id === id))
  )
    return <LoadingSpinner space={1} />;

  if (approved)
    return (
      <RequestCompleteNotice
        handler={completeHandler}
        title="公開申請完了"
        text={
          "公開申請が完了しました。\nこれより審査を開始します。\n結果通知までしばらくお待ちください。"
        }
        sub_text="※内容によっては数日お時間をいただく可能性がございます。"
      />
    );

  return (
    <FacilityForm
      facility={facility}
      imgItems={imgItems}
      traits={traits}
      types={types}
      requestState={requestState}
      isHasChanges={requestState === RequestState.Pending}
      error={error}
      handleInputChange={handleInputChange}
      deleteImgItem={deleteImgItem}
      handleTraits={changeTraits}
      handleTypes={changeTypes}
      handleSubmit={
        facility?.status === PageVisibility.NotPubliclyVisible
          ? handleSubmit
          : undefined
      }
      handleSave={handleSave}
    />
  );
};

export default EditFacilityContainer;
